import { useEffect, useMemo, useRef, useState } from 'react';
import { FiUpload, FiX, FiEye, FiAlertCircle, FiZap } from 'react-icons/fi';
import { useDicomUploaderManager } from "../../../../services/pacs-system";
import "./style.css";
import Progress from '@minohealth/common/components/progress/Progress';
import { DicomImageUploader } from '../../../../services/pacs-system/utils';
import { showWarningMessage } from '@minohealth/common/utils/notifiers';
import ChatImageViewerBase from '../../../chat-session-info/ChatImageViewerBase';
import useDialogOpenState from '@minohealth/common/hooks/useDialogOpenState';
import MissingFieldsModal from './MissingFieldsModal';
import { Button } from '../../../ui/button';
import { Switch } from '../../../ui/switch';
import { Label } from '../../../ui/label';

function UploadDicom() {
  const { fileInfoList, advancedMode, setAdvancedMode, addFile, removeFile, retryUpload } = useDicomUploaderManager();

  const sortedFileInfoList = useMemo(() => {
    const order = { generating: 0, retrying: 1, processing: 2, uploading: 3, 'pending-info': 4, queued: 5, completing: 6, waiting: 7, idle: 8, complete: 9, error: 10 }
    return [...fileInfoList].sort((a, b) => (order[getProgressStatus(a)] ?? 8) - (order[getProgressStatus(b)] ?? 8))
  }, [fileInfoList])

  const pendingFiles = useMemo(
    () => fileInfoList.filter(f => f.jobStatus === 'pending_info'),
    [fileInfoList]
  )

  const completedFiles = useMemo(
    () => fileInfoList.filter(f => f.jobStatus === 'complete'),
    [fileInfoList]
  )

  const [modalOpen, setModalOpen] = useState(false)
  const [modalInitialIndex, setModalInitialIndex] = useState(0)
  const autoPopupEnabledRef = useRef(true)
  // Count of freshPendingFiles at the last time the modal opened — auto-popup only fires for files beyond this
  const freshCountAtLastOpenRef = useRef(0)

  const freshPendingFiles = useMemo(
    () => pendingFiles.filter(f => !f.isServerSeeded),
    [pendingFiles]
  )
  useEffect(() => {
    if (freshPendingFiles.length > freshCountAtLastOpenRef.current && !modalOpen && autoPopupEnabledRef.current) {
      freshCountAtLastOpenRef.current = freshPendingFiles.length
      const newIdx = pendingFiles.findIndex(f => f.id === freshPendingFiles[freshPendingFiles.length - 1]?.id)
      setModalInitialIndex(newIdx >= 0 ? newIdx : 0)
      setModalOpen(true)
    }
  }, [freshPendingFiles.length, modalOpen])

  useEffect(() => {
    if (modalOpen && pendingFiles.length === 0) setModalOpen(false)
  }, [pendingFiles.length, modalOpen])

  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const imageViewerRef = useRef(null)
  const [_, setImageViewerOpen] = useDialogOpenState(imageViewerRef)
  const [selectedPreviewId, setSelectedPreviewId] = useState(null)

  const selectedFileInfoForPreview = fileInfoList.find(f => f.id === selectedPreviewId)

  useEffect(() => {
    if (selectedFileInfoForPreview != null) return;
    setImageViewerOpen(false)
  }, [selectedFileInfoForPreview])

  const isFileTypeAllowed = (file, acceptAttr) => {
    if (!acceptAttr) return true;
    const acceptedTypes = acceptAttr.split(',').map(t => t.trim().toLowerCase());
    if (acceptedTypes.includes(file.type.toLowerCase())) return true;
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (acceptedTypes.includes(ext)) return true;
    const primary = file.type.split('/')[0];
    if (acceptedTypes.includes(`${primary}/*`)) return true;
    return false;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = e.dataTransfer.files;
    const acceptAttr = fileInputRef.current?.accept;
    if (droppedFiles && droppedFiles.length > 0) {
      for (const file of droppedFiles) {
        if (isFileTypeAllowed(file, acceptAttr)) addFile(file);
        else showWarningMessage(`File rejected: ${file.name}. Type not allowed.`);
      }
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      for (const file of e.target.files) addFile(file);
    }
  };

  const handleDropAreaClick = () => fileInputRef.current.click();
  const handleClearCompleted = () => {
    for (const f of completedFiles) removeFile(f.id)
  };

  function openFilePreview(id) {
    setSelectedPreviewId(id)
    setImageViewerOpen(true)
  }

  function openModalForPacsId(pacsId) {
    autoPopupEnabledRef.current = true
    freshCountAtLastOpenRef.current = freshPendingFiles.length
    const idx = pendingFiles.findIndex(f => f.pacsId === pacsId)
    setModalInitialIndex(idx >= 0 ? idx : 0)
    setModalOpen(true)
  }

  return (
    <div className="upload-dicom">
      <div className="upload-dicom__actions">
        <div className="upload-dicom__actions-left">
          <div className="upload-dicom__advanced-wrapper">
            <div className="upload-dicom__advanced-header">
              <Label htmlFor="advanced-mode-switch" className="upload-dicom__advanced-label">
                Advanced Mode
              </Label>
              <Switch
                id="advanced-mode-switch"
                checked={advancedMode}
                onCheckedChange={setAdvancedMode}
              />
            </div>
            <p className="upload-dicom__advanced-desc">
              Provides more detailed analysis of the image. Incurs additional cost.
            </p>
          </div>
        </div>
        <div className="upload-dicom__actions-right">
          {completedFiles.length > 0 && (
            <Button variant="secondary" size="sm" onClick={handleClearCompleted}>
              <FiX />
              Clear completed
            </Button>
          )}
        </div>
      </div>

      <h2 className="upload-dicom__title">Upload DICOM Images*</h2>

      <div
        className={`upload-dicom__dropzone ${isDragging ? 'upload-dicom__dropzone--active' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleDropAreaClick}
      >
        {fileInfoList.length === 0 ? (
          <div className="upload-dicom__drop-content">
            <div className="upload-dicom__icon-container">
              <FiUpload className="upload-dicom__upload-icon" />
            </div>
            <p className="upload-dicom__drop-text">
              Drop your files here or <span className="upload-dicom__click-text">Click to upload</span>
            </p>
          </div>
        ) : (
          <div className="upload-dicom__files-list">
            {sortedFileInfoList.map(fileInfo => (
              <FileInfoItem
                fileInfo={fileInfo}
                previewFile={() => openFilePreview(fileInfo.id)}
                onFillDetails={() => openModalForPacsId(fileInfo.pacsId)}
                key={fileInfo.id}
              />
            ))}
          </div>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        multiple
        accept=".dcm,.dicom"
      />

      {selectedFileInfoForPreview != null && (
        <ChatImageViewerBase
          srcImage={selectedFileInfoForPreview.result?.data?.image}
          multiple={false}
          bboxes={[]}
          onCloseViewer={() => {
            setSelectedPreviewId(null);
            setImageViewerOpen(false);
          }}
          ref={imageViewerRef}
        />
      )}

      {modalOpen && pendingFiles.length > 0 && (
        <MissingFieldsModal
          pendingFiles={pendingFiles}
          initialIndex={modalInitialIndex}
          onClose={(completed = false) => {
            if (!completed) {
              autoPopupEnabledRef.current = false
            } else {
              freshCountAtLastOpenRef.current = 0
            }
            setModalOpen(false)
          }}
        />
      )}
    </div>
  );
}

/** @param {import('../../../../services/pacs-system').LeanDicomFileInfo} fileInfo */
function getProgressStatus(fileInfo) {
  if (fileInfo.uploadStatus === DicomImageUploader.Status.UPLOADING) {
    if (fileInfo.progress == null) return 'waiting'
    if (fileInfo.progress < 100) return 'uploading'
    return 'completing'
  }
  if (fileInfo.uploadStatus === DicomImageUploader.Status.ERROR) return 'error'
  if (fileInfo.uploadStatus === DicomImageUploader.Status.SUCCESS) {
    switch (fileInfo.jobStatus) {
      case 'pending_info':      return 'pending-info'
      case 'queued':            return 'queued'
      case 'processing':        return 'processing'
      case 'report_generating': return 'generating'
      case 'retrying':          return 'retrying'
      case 'complete':          return 'complete'
      case 'error':             return 'error'
      default:                  return 'complete'
    }
  }
  return 'idle'
}

/**
 * @param {Object} props
 * @param {import('../../../../services/pacs-system').LeanDicomFileInfo} props.fileInfo
 * @param {() => void} props.previewFile
 * @param {() => void} props.onFillDetails
 */
function FileInfoItem({ fileInfo, previewFile, onFillDetails }) {
  const { removeFile, retryUpload } = useDicomUploaderManager()

  const progressStatus = getProgressStatus(fileInfo)

  const progressValue = (() => {
    switch (progressStatus) {
      case 'waiting':      return 0
      case 'uploading':    return fileInfo.progress
      case 'completing':   return undefined
      case 'pending-info': return 100
      case 'queued':       return undefined
      case 'processing':   return undefined
      case 'generating':   return undefined
      case 'retrying':     return undefined
      case 'complete':     return 100
      case 'error':        return 100
      default:             return 0
    }
  })()

  const statusText = (() => {
    switch (progressStatus) {
      case 'idle':         return null
      case 'waiting':      return 'Waiting...'
      case 'uploading':    return 'Uploading...'
      case 'completing':   return 'Completing upload...'
      case 'pending-info': return 'Awaiting patient info'
      case 'queued':       return 'Queued for processing'
      case 'processing':   return 'Processing...'
      case 'generating':   return 'Generating report...'
      case 'retrying':     return `Retrying (attempt ${fileInfo.jobRetryAttempt}/3)...`
      case 'complete':     return 'Complete'
      case 'error':        return fileInfo.error?.message ?? fileInfo.jobError ?? 'Failed'
      default:             return null
    }
  })()

  const isError = progressStatus === 'error'

  return (
    <div
      className="upload-dicom__file-item"
      data-error={isError}
      onClick={e => e.stopPropagation()}
    >
      <div className="upload-dicom__file-info">
        <div className="upload-dicom__file-name-row">
          <span className="upload-dicom__file-name" title={fileInfo.file.name}>
            {fileInfo.file.name}
          </span>
          {fileInfo.advancedMode && (
            <span className="upload-dicom__advanced-badge" title="Advanced mode">
              <FiZap />
            </span>
          )}
        </div>
        {statusText && (
          <span className="upload-dicom__file-status-hint" data-error={isError || undefined}>
            {statusText}
          </span>
        )}
      </div>

      {fileInfo.jobStatus === 'pending_info' && (
        <Button
          variant="secondary"
          size="sm"
          className="upload-dicom__fill-details-btn"
          onClick={onFillDetails}
        >
          <FiAlertCircle />
          Add info
        </Button>
      )}

      <Progress
        className="upload-dicom__file-progress-bar"
        data-status={progressStatus}
        value={progressValue}
        showValue={false}
      />

      <button
        className="upload-dicom__file-cancel"
        onClick={() => removeFile(fileInfo.id)}
        title="Remove"
      >
        <FiX />
      </button>
      <button
        className="upload-dicom__file-view"
        onClick={previewFile}
        title="View file"
        disabled={!fileInfo.result?.data?.image}
      >
        <FiEye />
      </button>

      {isError && (
        <button className="upload-dicom__file-report" onClick={() => retryUpload(fileInfo.id)}>
          Retry
        </button>
      )}
    </div>
  );
}

export default UploadDicom;
